// src/components/admin/StudentTable.jsx
import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { collection, getDocs, deleteDoc, doc, updateDoc } from "firebase/firestore";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";




const StudentTable = () => {
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [editingStudent, setEditingStudent] = useState(null);

  // Fetch students
  const fetchStudents = async () => {
    const querySnapshot = await getDocs(collection(db, "students"));
    const studentList = [];
    querySnapshot.forEach((d) => {
      studentList.push({ id: d.id, ...d.data() });
    });
    setStudents(studentList);
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  // Delete student
  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "students", id));
    fetchStudents();
  };

  // Select student checkbox
  const toggleSelect = (id) => {
    setSelectedStudents((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  // Generate Excel
  const handleGenerateExcel = () => {
    const worksheetData = students.map((s, index) => {
      const { status } = getPaymentStatus(s.fees);

      return {
        "#": index + 1,
        Name: `${s.firstName} ${s.lastName}`,
        Address: s.address,
        Email: s.email,
        Phone: s.phone,
        Course: s.course,
        Fees: s.fees,
        "Fees Completion Status": status,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");
    XLSX.writeFile(workbook, "students.xlsx");
  };




  // Generate Receipt PDF (single student)
 const generateReceiptPDF = (student) => {
  const doc = new jsPDF();

  // Institute Header (moved down)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("PLACIFY INSTITUTE", 105, 30, { align: "center" });

  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.text("244-B, Silicon City, First Floor, Indore", 105, 38, { align: "center" });
  doc.text("Phone: +91 98765 43210 | Email: info@placify.com", 105, 44, { align: "center" });

  // Receipt Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("FEE RECEIPT", 105, 60, { align: "center" });

  // Receipt Border
  doc.setLineWidth(0.5);
  doc.rect(15, 20, 180, 260); // outer border (shifted down a bit)

  // Date
  const today = new Date().toLocaleDateString();
  doc.setFontSize(10);
  doc.text(`Date: ${today}`, 160, 65);

  // Student Info
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Student Information", 20, 80);
  doc.setLineWidth(0.2);
  doc.line(20, 83, 80, 83);

  doc.setFont("helvetica", "normal");
  doc.text(`Name: ${student.firstName} ${student.lastName}`, 20, 95);
  doc.text(`Course: ${student.course || "N/A"}`, 20, 105);
  doc.text(`Email: ${student.email || "N/A"}`, 20, 115);
  doc.text(`Phone: ${student.phone || "N/A"}`, 20, 125);
  doc.text(`Address: ${student.address || "N/A"}`, 20, 135);

  // Payment Info
  doc.setFont("helvetica", "bold");
  doc.text("Payment Details", 20, 155);
  doc.line(20, 158, 80, 158);

  const { status, remaining } = getPaymentStatus(student.fees);

  doc.setFont("helvetica", "normal");
  doc.text(`Fees Paid: ${student.fees}`, 20, 170);
  doc.text(`Payment Status: ${status}`, 20, 180);
  doc.text(`Remaining Fees: ${remaining}`, 20, 190);

  // Signature
  doc.line(150, 240, 190, 240);
  doc.text("Authorized Signature", 150, 250);

  // Footer
  doc.setFontSize(9);
  doc.setTextColor(100);
  

  doc.save(`${student.firstName}_${student.lastName}_receipt.pdf`);
};




  // Handle multiple receipts
  const handleGenerateFeesPDF = () => {
    const selected = students.filter((s) => selectedStudents.includes(s.id));

    if (selected.length === 0) {
      alert("Please select at least one student to generate receipt.");
      return;
    }

    selected.forEach((student) => generateReceiptPDF(student));
  };


  // Save edited student
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const studentRef = doc(db, "students", editingStudent.id);
    await updateDoc(studentRef, editingStudent);
    setEditingStudent(null);
    fetchStudents();
  };

  const getPaymentStatus = (fees) => {
    if (!fees) return { status: "Pending", remaining: "N/A" };

    if (fees.includes("One Time")) {
      return { status: "Completed", remaining: "₹0" };
    }
    if (fees.includes("First Installment")) {
      return { status: "Pending", remaining: "₹2000" };
    }
    if (fees.includes("Second Installment")) {
      return { status: "Completed", remaining: "₹0" };
    }

    return { status: "Pending", remaining: "N/A" };
  };


  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Registered Students</h2>
      <table className="w-full border text-sm">
        <thead>
          <tr className="bg-gray-200">
            <th className="border px-3 py-2">Select</th>
            <th className="border px-3 py-2">Name</th>
            <th className="border px-3 py-2">Address</th>
            <th className="border px-3 py-2">Email</th>
            <th className="border px-3 py-2">Phone</th>
            <th className="border px-3 py-2">Course</th>
            <th className="border px-3 py-2">Fees</th>
            <th className="border px-3 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {students.map((s) => (
            <tr key={s.id} className="text-center">
              <td className="border px-3 py-2">
                <input
                  type="checkbox"
                  checked={selectedStudents.includes(s.id)}
                  onChange={() => toggleSelect(s.id)}
                />
              </td>
              <td className="border px-3 py-2">
                {s.firstName} {s.lastName}
              </td>
              <td className="border px-3 py-2">{s.address}</td>
              <td className="border px-3 py-2">{s.email}</td>
              <td className="border px-3 py-2">{s.phone}</td>
              <td className="border px-3 py-2">{s.course}</td>
              <td className="border px-3 py-2">{s.fees}</td>
              <td className="border px-3 py-2">
                <button
                  className="text-blue-600 mr-2"
                  onClick={() => setEditingStudent(s)}
                >
                  Edit
                </button>
                <button
                  className="text-red-600"
                  onClick={() => handleDelete(s.id)}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Buttons */}
      <div className="flex gap-4 mt-4">
        <button
          onClick={handleGenerateExcel}
          className="bg-yellow-500 text-white px-4 py-2 rounded"
        >
          Generate Excel
        </button>
        <button
          onClick={handleGenerateFeesPDF}
          className="bg-purple-600 text-white px-4 py-2 rounded"
        >
          Generate Fees Receipt
        </button>

      </div>

      {/* Edit Modal */}
      {editingStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Edit Student</h3>
            <form onSubmit={handleEditSubmit} className="space-y-3">
              <input
                type="text"
                className="w-full border px-3 py-2 rounded"
                value={editingStudent.firstName}
                onChange={(e) =>
                  setEditingStudent({ ...editingStudent, firstName: e.target.value })
                }
              />
              <input
                type="text"
                className="w-full border px-3 py-2 rounded"
                value={editingStudent.lastName}
                onChange={(e) =>
                  setEditingStudent({ ...editingStudent, lastName: e.target.value })
                }
              />
              <input
                type="text"
                className="w-full border px-3 py-2 rounded"
                value={editingStudent.address}
                onChange={(e) =>
                  setEditingStudent({ ...editingStudent, address: e.target.value })
                }
              />
              <input
                type="email"
                className="w-full border px-3 py-2 rounded"
                value={editingStudent.email}
                onChange={(e) =>
                  setEditingStudent({ ...editingStudent, email: e.target.value })
                }
              />
              <input
                type="text"
                className="w-full border px-3 py-2 rounded"
                value={editingStudent.phone}
                onChange={(e) =>
                  setEditingStudent({ ...editingStudent, phone: e.target.value })
                }
              />
              <input
                type="text"
                className="w-full border px-3 py-2 rounded"
                value={editingStudent.course}
                onChange={(e) =>
                  setEditingStudent({ ...editingStudent, course: e.target.value })
                }
              />
              <select
                className="w-full border px-3 py-2 rounded"
                value={editingStudent.fees}
                onChange={(e) =>
                  setEditingStudent({ ...editingStudent, fees: e.target.value })
                }
              >
                <option value="4500 (One Time)">One Time (₹4500)</option>
                <option value="3000 (First Installment)">First Installment (₹3000)</option>
                <option value="2000 (Second Installment)">Second Installment (₹2000)</option>
              </select>


              <div className="flex justify-end gap-3 mt-4">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-400 rounded"
                  onClick={() => setEditingStudent(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentTable;
